/**
 * Phase 24.4b Step 6 — recipe detail modal with mark-cooked + feedback +
 * swap actions.
 *
 * Optimistic mark-cooked + feedback (S4 Round 3 Q5): mutation fires +
 * we toggle the local cache row immediately. Server confirms; on
 * failure we rollback. Swap is non-optimistic (separate dialog).
 *
 * focus-trap-react comes from the Modal primitive (S4a.2 Step 0.5a).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Heart, ThumbsDown, Flame, Replace, Maximize2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Skeleton } from "@/components/ui/Skeleton";
import { Button } from "@/components/ui/Button";
import { RecipeDetailContent } from "@/components/meal_plan/RecipeDetailContent";
import { useToast } from "@/stores/toast";
import { useMealPlanUi } from "@/stores/meal_plan_ui";
import {
  ALLOWED_SERVINGS,
  markMealPlanItemCooked,
  updateMealPlanItemServings,
  type AllowedServings,
  type MealPlanRead,
  type MealPlanItemRead,
} from "@/api/meal_plan";
import { getRecipe, submitRecipeFeedback } from "@/api/recipes";
import { cn } from "@/lib/utils";

interface Props {
  planId: number;
  itemId: number;
  recipeId: number;
  onClose: () => void;
}

export function RecipeDetailModal({
  planId,
  itemId,
  recipeId,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { show } = useToast();
  const { setSwapDialogOpen } = useMealPlanUi();

  const recipeQ = useQuery({
    queryKey: ["recipe", recipeId],
    queryFn: () => getRecipe(recipeId),
    staleTime: 5 * 60_000,
  });

  // Find the meal-plan item from cache to read is_cooked state without
  // a second network round-trip. Falls back to false if not found.
  const planCache = qc.getQueryData<MealPlanRead>(["meal-plan-current"]);
  const item: MealPlanItemRead | undefined = planCache?.days
    .flatMap((d) => d.items)
    .find((i) => i.id === itemId);
  const isCooked = item?.is_cooked ?? false;

  const cookedMut = useMutation({
    mutationFn: (next: boolean) => markMealPlanItemCooked(planId, itemId, next),
    // Optimistic — flip the cache row immediately
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ["meal-plan-current"] });
      const prev = qc.getQueryData<MealPlanRead>(["meal-plan-current"]);
      if (prev) {
        const optimistic: MealPlanRead = {
          ...prev,
          days: prev.days.map((d) => ({
            ...d,
            items: d.items.map((i) =>
              i.id === itemId
                ? {
                    ...i,
                    is_cooked: next,
                    cooked_at: next ? new Date().toISOString() : null,
                  }
                : i,
            ),
          })),
        };
        qc.setQueryData(["meal-plan-current"], optimistic);
      }
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      // Rollback
      if (ctx?.prev) qc.setQueryData(["meal-plan-current"], ctx.prev);
      show({ message: t("meal_plan.mark_cooked_failed"), variant: "error" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["meal-plan-current"] });
    },
  });

  const feedbackMut = useMutation({
    mutationFn: (action: "like" | "dislike") =>
      submitRecipeFeedback(recipeId, action),
    onSuccess: (data) => {
      show({
        message: t(
          data.action_taken === "like"
            ? "meal_plan.recipe_liked"
            : "meal_plan.recipe_disliked",
        ),
        variant: "success",
      });
    },
    onError: () => {
      show({ message: t("meal_plan.feedback_failed"), variant: "error" });
    },
  });

  // Phase 24.7.4 T4 — servings selector. Local state shadows item.servings
  // so the optimistic UI flips immediately on click; mutation reconciles
  // (or rolls back) shortly after. Falls back to 1.0 if no item context.
  const [localServings, setLocalServings] = useState<AllowedServings>(
    (item?.servings as AllowedServings) ?? 1.0,
  );
  useEffect(() => {
    if (item?.servings != null) {
      setLocalServings(item.servings as AllowedServings);
    }
  }, [item?.servings]);

  const servingsMut = useMutation({
    mutationFn: (next: AllowedServings) =>
      updateMealPlanItemServings(planId, itemId, next),
    onMutate: async (next) => {
      await qc.cancelQueries({ queryKey: ["meal-plan-current"] });
      const prev = qc.getQueryData<MealPlanRead>(["meal-plan-current"]);
      if (prev) {
        const optimistic: MealPlanRead = {
          ...prev,
          days: prev.days.map((d) => ({
            ...d,
            items: d.items.map((i) => {
              if (i.id !== itemId) return i;
              const factor = i.servings > 0 ? next / i.servings : next;
              return {
                ...i,
                servings: next,
                planned_kcal: Math.round(i.planned_kcal * factor),
                planned_protein_g:
                  Math.round(i.planned_protein_g * factor * 10) / 10,
                planned_carbs_g:
                  Math.round(i.planned_carbs_g * factor * 10) / 10,
                planned_fat_g: Math.round(i.planned_fat_g * factor * 10) / 10,
              };
            }),
          })),
        };
        qc.setQueryData(["meal-plan-current"], optimistic);
      }
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev) {
        qc.setQueryData(["meal-plan-current"], ctx.prev);
      }
      setLocalServings(
        (ctx?.prev?.days
          .flatMap((d) => d.items)
          .find((i) => i.id === itemId)?.servings as AllowedServings) ?? 1.0,
      );
      show({ message: t("meal_plan.servings_save_failed"), variant: "error" });
    },
    onSuccess: () => {
      show({ message: t("meal_plan.servings_saved"), variant: "success" });
      qc.invalidateQueries({ queryKey: ["shopping-list"] });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["meal-plan-current"] });
    },
  });

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={recipeQ.data?.name}
      maxWidth="max-w-2xl"
    >
      <div data-testid="recipe-detail-modal">
        {recipeQ.isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : recipeQ.isError || !recipeQ.data ? (
          <p className="text-[13px] text-coral">
            {t("meal_plan.recipe_load_failed")}
          </p>
        ) : (
          <div className="space-y-4">
            {/* Phase 24.7.4 T4 — servings selector. Lives above shared content
                so the live multiplier visually anchors к the macros + ingredients
                that follow. Hidden when no plan-item context (item==undefined). */}
            {item ? (
              <div
                className="rounded-md border border-cream-border bg-cream-light p-3"
                data-testid="servings-selector"
              >
                <div className="mb-2 flex items-center justify-between text-[12px]">
                  <span className="font-medium text-navy-deep">
                    {t("meal_plan.servings_label")}
                  </span>
                  <span className="metric-number text-amber-dark">
                    ×{localServings}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {ALLOWED_SERVINGS.map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => {
                        if (opt === localServings) return;
                        setLocalServings(opt);
                        servingsMut.mutate(opt);
                      }}
                      disabled={servingsMut.isPending}
                      className={cn(
                        "flex-1 rounded-md py-1.5 text-[12px] transition-colors",
                        localServings === opt
                          ? "bg-amber/20 font-medium text-amber-dark"
                          : "bg-white text-taupe hover:bg-cream-border/40",
                      )}
                      data-testid={`servings-${opt}`}
                    >
                      {t(`meal_plan.servings.option_${String(opt).replace(".", "_")}`)}
                    </button>
                  ))}
                </div>
                <div className="mt-1.5 text-[10px] text-taupe">
                  {t("meal_plan.servings_hint")}
                </div>
              </div>
            ) : null}

            {/* Phase 24.7.4 T2 — shared content, also rendered standalone at /recipes/:id */}
            {/* Phase 24.7.4 T4 — pass localServings so amounts/macros scale live */}
            <RecipeDetailContent
              recipe={recipeQ.data}
              servingsMultiplier={localServings}
            />

            {/* Actions */}
            <div className="flex flex-wrap gap-2 border-t border-cream-border pt-3">
              <Button
                variant={isCooked ? "primary" : "secondary"}
                size="sm"
                onClick={() => cookedMut.mutate(!isCooked)}
                disabled={cookedMut.isPending}
                data-testid="action-mark-cooked"
              >
                <Flame className="h-3.5 w-3.5" strokeWidth={2} />
                {isCooked
                  ? t("meal_plan.unmark_cooked")
                  : t("meal_plan.mark_cooked")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => feedbackMut.mutate("like")}
                disabled={feedbackMut.isPending}
                data-testid="action-like"
              >
                <Heart className="h-3.5 w-3.5" strokeWidth={2} />
                {t("meal_plan.like")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => feedbackMut.mutate("dislike")}
                disabled={feedbackMut.isPending}
                data-testid="action-dislike"
              >
                <ThumbsDown className="h-3.5 w-3.5" strokeWidth={2} />
                {t("meal_plan.dislike")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onClose();
                  navigate(`/recipes/${recipeId}`);
                }}
                data-testid="action-open-fullscreen"
              >
                <Maximize2 className="h-3.5 w-3.5" strokeWidth={2} />
                {t("meal_plan.open_fullscreen")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setSwapDialogOpen(true)}
                className={cn("ml-auto")}
                data-testid="action-swap"
              >
                <Replace className="h-3.5 w-3.5" strokeWidth={2} />
                {t("meal_plan.swap")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
